'use client';

import Image from "next/image";
import { AnimateOnScroll } from "@/components/animate-on-scroll";
import ClientOnly from "../client-only";
import CalendlyWidget from "./calendly-widget";
import { Button } from "../ui/button";
import { useScheduleDemo } from "@/context/schedule-demo-context";
import { useEffect } from "react";

export default function ContactSection() {
  const { shouldShow, showDemoScheduler } = useScheduleDemo();

  useEffect(() => {
    // This effect checks for a URL parameter to show the calendar.
    // It's designed for links coming from external pages, like the blog.
    const params = new URLSearchParams(window.location.search);
    if (params.get('schedule') === 'true') {
      showDemoScheduler();
      // Optional: scroll to the contact section if the hash isn't handled automatically
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView();
      }
    }
  }, [showDemoScheduler]);


  return (
    <section id="contact" className="w-full py-16 overflow-hidden">
      <div className="container relative mx-auto max-w-screen-lg px-4 md:px-8">
        <div className="text-center mb-12">
            <AnimateOnScroll 
                animationClassName="animate-fade-in-up"
                className="flex flex-col items-center justify-center space-y-4 text-center"
                umamiEventName="View Section: Contact"
                >
                <h2 className="text-balance text-3xl font-bold font-headline tracking-tighter sm:text-4xl md:text-5xl">
                    Schedule Your Live Demo
                </h2>
                <p className="max-w-[600px] text-muted-foreground md:text-xl/relaxed">
                    See our AI on your charts in a focused 30-minute working session — no commitment. We&apos;ll show your RAF &amp; revenue lift, the claim-to-evidence audit trail, and how fast you go live.
                </p>
                <p className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm font-medium text-muted-foreground pt-1">
                    <span>✓ 30 minutes</span><span>✓ No commitment</span><span>✓ See your own numbers</span>
                </p>
            </AnimateOnScroll>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start lg:items-center">
          <AnimateOnScroll 
            animationClassName="animate-fade-in"
            style={{ animationDelay: "200ms" }}
          >
            <div className="w-full mx-auto lg:mx-0 rounded-lg overflow-hidden ring-1 ring-primary/10 shadow-lg bg-card">
              <div className={shouldShow ? "h-[700px] overflow-hidden" : "overflow-hidden"}>
                <ClientOnly>
                  {shouldShow ? (
                    <CalendlyWidget />
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-muted/20">
                       <h3 className="text-2xl font-bold font-headline mb-4">Ready to See It Live?</h3>
                       <p className="text-muted-foreground mb-6 max-w-sm">
                          Click the button below to open the scheduling calendar and book your personalized demo instantly.
                       </p>
                       <Button size="lg" onClick={showDemoScheduler}>
                          Schedule Your Live Demo
                       </Button>
                       <p className="text-xs text-muted-foreground mt-4">30 minutes · no commitment · HIPAA-compliant</p>
                    </div>
                  )}
                </ClientOnly>
              </div>
            </div>
          </AnimateOnScroll>

          <AnimateOnScroll
            animationClassName="animate-fade-in"
            style={{ animationDelay: "400ms" }}
            className="hidden lg:flex items-center justify-center"
          >
            <Image
              src="/contact-image.webp"
              alt="A MedChartScan specialist guides a client through the AI-powered risk adjustment platform during a live demo."
              width={600}
              height={600}
              className="w-full h-[700px] rounded-lg opacity-90 object-cover ring-1 ring-primary/10 shadow-lg"
              data-ai-hint="healthcare meeting"
            />
          </AnimateOnScroll>
        </div>
      </div>
    </section>
  );
}
